import React, { useCallback, useState } from 'react';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import CircularProgress from '@material-ui/core/CircularProgress';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import { patchUserTos } from '../../../Core/Api/userAPI';
import { breakpoints } from '../../../Core/Theme';

const TosUpdateModal = ({ platformUser, classes }) => {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [hasError, setHasError] = useState(false);

  const isLoading = !platformUser || platformUser.loading;
  const isOpen =
    !isLoading && !!platformUser.requiresTosAcceptance && !isDismissed;

  const handleAcknowledge = useCallback(async () => {
    // eslint-disable-next-line no-underscore-dangle
    const userId = platformUser && platformUser._id;
    if (!userId || isAcknowledging) {
      return;
    }

    setIsAcknowledging(true);
    setHasError(false);
    try {
      await patchUserTos(userId, new Date().toISOString());
      setIsDismissed(true);
    } catch {
      setHasError(true);
    } finally {
      setIsAcknowledging(false);
    }
  }, [platformUser, isAcknowledging]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      aria-labelledby="tos-update-modal-title"
      aria-describedby="tos-update-modal-description"
      open={isOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      className={classes.modal}
      style={{ zIndex: 1500 }}
    >
      <Fade in={isOpen}>
        <div className={classes.paper}>
          <h2 id="tos-update-modal-title" className={classes.title}>
            {t('tosUpdateModal.title')}
          </h2>

          <div className={classes.bodyContainer}>
            <p id="tos-update-modal-description" className={classes.body}>
              {t('tosUpdateModal.bodyParagraph1')}
            </p>
            <p className={classes.body}>{t('tosUpdateModal.bodyParagraph2')}</p>
          </div>

          <div className={classes.links}>
            <a
              href="https://www.industriousoffice.com/site-portal-terms"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {t('tosUpdateModal.termsLink')}
            </a>
            <span className={classes.separator}>&middot;</span>
            <a
              href="https://www.industriousoffice.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {t('tosUpdateModal.privacyLink')}
            </a>
          </div>

          {hasError && (
            <p className={classes.errorText}>{t('tosUpdateModal.error')}</p>
          )}

          <div className={classes.actions}>
            <button
              type="button"
              onClick={handleAcknowledge}
              disabled={isAcknowledging}
              className={classes.button}
            >
              {isAcknowledging && (
                <CircularProgress size={16} className={classes.buttonSpinner} />
              )}
              {t('tosUpdateModal.cta')}
            </button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

TosUpdateModal.defaultProps = {
  platformUser: null,
};

TosUpdateModal.propTypes = {
  platformUser: PropTypes.shape({
    _id: PropTypes.string,
    loading: PropTypes.bool,
    requiresTosAcceptance: PropTypes.bool,
  }),
  classes: PropTypes.shape({}).isRequired,
};

const styles = theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 16px',
  },
  paper: {
    backgroundColor: '#fff',
    boxShadow: '0px 8px 32px 0px rgba(0,0,0,0.12)',
    borderRadius: 16,
    padding: '32px',
    maxWidth: 540,
    width: '100%',
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    [theme.breakpoints.down(breakpoints.MOBILE)]: {
      maxHeight: '100vh',
      borderRadius: 0,
      overflowY: 'auto',
    },
  },
  title: {
    margin: 0,
    fontSize: '1.375rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#1a1a1a',
  },
  bodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  body: {
    margin: 0,
    fontSize: '0.875rem',
    lineHeight: 1.7,
    color: '#4a4a4a',
  },
  links: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0px',
    [theme.breakpoints.down(breakpoints.MOBILE)]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '4px',
    },
  },
  separator: {
    margin: '0 8px',
    color: '#4a4a4a',
    fontSize: '0.875rem',
    [theme.breakpoints.down(breakpoints.MOBILE)]: {
      display: 'none',
    },
  },
  link: {
    fontSize: '0.875rem',
    color: '#013e3f',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    [theme.breakpoints.down(breakpoints.MOBILE)]: {
      justifyContent: 'stretch',
    },
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 32px',
    backgroundColor: '#013e3f',
    color: '#fff',
    fontWeight: 500,
    fontSize: '0.875rem',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    [theme.breakpoints.down(breakpoints.MOBILE)]: {
      width: '100%',
      padding: '14px 32px',
    },
  },
  buttonSpinner: {
    color: '#fff',
  },
  errorText: {
    margin: 0,
    fontSize: '0.75rem',
    color: '#d32f2f',
  },
});

export default withStyles(styles)(TosUpdateModal);
